import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './SinglePost.css';

function SinglePost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts/${slug}`);
      
      if (!response.ok) {
        throw new Error('Post not found');
      }

      const data = await response.json();
      setPost(data);

      // Fetch related posts (same category)
      const relatedRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/posts?category=${encodeURIComponent(data.category)}&limit=3`
      );
      const relatedData = await relatedRes.json();
      setRelatedPosts(relatedData.posts.filter(p => p.slug !== slug));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryBadgeClass = (category) => {
    const badges = {
      'RPGs': 'badge-purple',
      'Walkthroughs': 'badge-blue',
      'Tips & Tricks': 'badge-cyan',
      'Reviews': 'badge-magenta',
      'Gear & Setups': 'badge-blue',
      'News': 'badge-cyan'
    };
    return badges[category] || 'badge-purple';
  };

  const sharePost = (platform) => {
    const url = window.location.href;
    const text = post.title;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      reddit: `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`,
      bluesky: `https://bsky.app/intent/compose?text=${encodeURIComponent(text + ' ' + url)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="error-container" data-testid="error-page">
        <h2>Post Not Found</h2>
        <p>The post you're looking for doesn't exist.</p>
        <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
      </div>
    );
  }

  return (
    <main className="single-post" data-testid="single-post-page">
      <div className="container">
        {/* Back Button */}
        <Link to="/blog" className="back-link" data-testid="back-link">
          ← Back to Blog
        </Link>

        {/* Post Header */}
        <article className="post-article" data-testid="post-article">
          <header className="post-header" data-testid="post-header">
            <span className={`badge ${getCategoryBadgeClass(post.category)}`}>
              {post.category}
            </span>
            <h1 className="post-title" data-testid="post-title">
              {post.title}
            </h1>
            <div className="post-meta" data-testid="post-meta">
              <span>By {post.author}</span>
              <span>•</span>
              <span>{new Date(post.publish_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="post-featured-image" data-testid="post-image">
            <img src={post.featured_image} alt={post.title} />
          </div>

          {/* Post Content */}
          <div 
            className="post-body" 
            data-testid="post-body"
            dangerouslySetInnerHTML={{ __html: post.body }}
          />

          {/* Share Buttons */}
          <div className="post-share" data-testid="post-share">
            <h3>Share this post</h3>
            <div className="share-buttons">
              <button 
                className="share-btn twitter"
                onClick={() => sharePost('twitter')}
                data-testid="share-twitter"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X (Twitter)
              </button>
              <button 
                className="share-btn reddit"
                onClick={() => sharePost('reddit')}
                data-testid="share-reddit"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                </svg>
                Reddit
              </button>
              <button 
                className="share-btn facebook"
                onClick={() => sharePost('facebook')}
                data-testid="share-facebook"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>
              <button 
                className="share-btn linkedin"
                onClick={() => sharePost('linkedin')}
                data-testid="share-linkedin"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </button>
            </div>
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="related-posts" data-testid="related-posts">
            <h2 className="section-title">Related Posts</h2>
            <div className="related-grid">
              {relatedPosts.map((relatedPost, index) => (
                <Link
                  key={relatedPost._id}
                  to={`/blog/${relatedPost.slug}`}
                  className="related-card glass-card"
                  data-testid={`related-post-${index}`}
                >
                  <div className="related-image">
                    <img src={relatedPost.featured_image} alt={relatedPost.title} />
                  </div>
                  <div className="related-content">
                    <span className={`badge ${getCategoryBadgeClass(relatedPost.category)}`}>
                      {relatedPost.category}
                    </span>
                    <h3 className="related-title">{relatedPost.title}</h3>
                    <p className="related-excerpt">{relatedPost.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}

export default SinglePost;
